<template>
  <app-page labelledby="page-title">
    <page-header
      :title="roleTitle || 'Рабочее место'"
      description="Сотрудник, приглашение и права доступа рабочего места."
    >
      <template #status>
        <status-badge :label="isRootRole ? 'Системное' : occupantStatus" :tone="isRootRole ? 'info' : occupantTone" :icon="isRootRole ? 'verified_user' : 'badge'" />
      </template>
      <template #actions>
        <q-btn flat no-caps label="К структуре" :to="{ name: 'org_main' }" />
      </template>
    </page-header>

    <app-state v-if="state !== 'ready'" :state="state" :title="stateTitle" :message="stateMessage" @retry="requestRole" />
    <template v-else>
      <page-section title="Сотрудник" description="Одному рабочему месту соответствует один сотрудник или одно активное приглашение.">
        <div v-if="currentUser.id > 0" class="occupant-row">
          <q-avatar color="primary" text-color="white">{{ userInitials }}</q-avatar>
          <div class="occupant-row__content">
            <strong>{{ currentUser.display_name || currentUser.email }}</strong>
            <span>{{ currentUser.email }}</span>
          </div>
          <q-btn v-if="!isRootRole" flat no-caps color="negative" label="Исключить" @click="openConfirm('remove-user')" />
        </div>
        <div v-else-if="hasInvite" class="occupant-row">
          <q-icon name="mail_outline" size="2rem" color="warning" />
          <div class="occupant-row__content">
            <strong>Приглашение отправлено</strong>
            <span>{{ inviteDisplayName || oldInviteEmail }} · {{ oldInviteEmail }}</span>
          </div>
          <div class="occupant-row__actions">
            <q-btn flat no-caps label="Скопировать ссылку" @click="copyInviteLink" />
            <q-btn flat no-caps color="negative" label="Отозвать" @click="openConfirm('cancel-invite')" />
          </div>
        </div>
        <form v-else class="invite-form" @submit.prevent="inviteUser">
          <q-input v-model.trim="inviteFirstName" label="Имя" />
          <q-input v-model.trim="inviteLastName" label="Фамилия" />
          <q-input v-model.trim="inviteEmail" type="email" label="Email" :rules="[(value) => !!value || 'Введите email', validateEmail]" />
          <q-btn color="primary" no-caps type="submit" label="Пригласить" :loading="actionPending" :disable="!isInviteValid" />
        </form>
      </page-section>

      <form @submit.prevent="saveRole">
        <page-section title="Настройки" description="Название отображается в структуре и приглашениях.">
          <q-input v-model.trim="roleTitle" class="role-title" label="Название рабочего места" maxlength="120" :readonly="isRootRole" />
        </page-section>

        <page-section title="Права доступа" description="Данные старых ролей нормализуются при открытии: редактирование всегда включает просмотр.">
          <role-permissions-editor v-model="permissions" :disabled="isRootRole" test-id-prefix="role" />
        </page-section>

        <div class="role-actions">
          <q-btn v-if="!isRootRole" flat no-caps color="negative" label="Удалить рабочее место" @click="openConfirm('delete-role')" />
          <q-btn color="primary" no-caps type="submit" label="Сохранить" :loading="saving" :disable="!roleTitle || isRootRole" />
        </div>
      </form>
    </template>

    <confirm-dialog
      v-model="confirmOpen"
      :title="confirmContent.title"
      :body="confirmContent.body"
      :confirm-label="confirmContent.label"
      destructive
      :pending="actionPending"
      @cancel="confirmOpen = false"
      @confirm="confirmAction"
    />
  </app-page>
</template>

<script>
import AppPage from '../../../components/ui/AppPage.vue'
import AppState from '../../../components/ui/AppState.vue'
import ConfirmDialog from '../../../components/ui/ConfirmDialog.vue'
import PageHeader from '../../../components/ui/PageHeader.vue'
import PageSection from '../../../components/ui/PageSection.vue'
import StatusBadge from '../../../components/ui/StatusBadge.vue'
import RolePermissionsEditor, { normalizeRolePermissions } from '../../../components/org/RolePermissionsEditor.vue'

const EMPTY_USER = Object.freeze({ id: -1, email: '', display_name: '' })

export default {
  name: 'RoleSettings',
  components: { AppPage, AppState, ConfirmDialog, PageHeader, PageSection, RolePermissionsEditor, StatusBadge },
  data() {
    return {
      state: 'loading', stateTitle: '', stateMessage: '', roleId: this.$route.params.id,
      roleTitle: '', permissions: normalizeRolePermissions(), isRootRole: false,
      currentUser: { ...EMPTY_USER }, hasInvite: false, oldInviteEmail: '', oldInviteToken: '', inviteDisplayName: '',
      inviteFirstName: '', inviteLastName: '', inviteEmail: '', saving: false, actionPending: false,
      confirmOpen: false, confirmType: ''
    }
  },
  computed: {
    occupantStatus() { return this.currentUser.id > 0 ? 'Занято' : (this.hasInvite ? 'Приглашение' : 'Вакантно') },
    occupantTone() { return this.currentUser.id > 0 ? 'success' : (this.hasInvite ? 'warning' : 'neutral') },
    userInitials() { return (this.currentUser.display_name || this.currentUser.email || '?').slice(0, 2).toUpperCase() },
    isInviteValid() { return this.validateEmailValue(this.inviteEmail) },
    confirmContent() {
      const content = {
        'delete-role': ['Удалить рабочее место?', 'Сотрудник потеряет доступ к организации, а приглашение будет отозвано.', 'Удалить'],
        'remove-user': ['Исключить сотрудника?', 'Сотрудник будет исключен из организации и потеряет доступ к данным.', 'Исключить'],
        'cancel-invite': ['Отозвать приглашение?', 'Ссылка приглашения перестанет действовать.', 'Отозвать']
      }[this.confirmType] || ['', '', 'Подтвердить']
      return { title: content[0], body: content[1], label: content[2] }
    }
  },
  created() {
    this.onRole = this.applyRole
    this.onUpdated = (response) => { this.saving = false; this.handleMutation(response, 'Рабочее место сохранено', true) }
    this.onInvited = (response) => { this.actionPending = false; this.handleMutation(response, 'Приглашение отправлено', true) }
    this.onDeleted = (response) => { this.actionPending = false; if (this.handleMutation(response, 'Рабочее место удалено')) this.$router.push({ name: 'org_main' }) }
    this.onInviteCancelled = (response) => { this.actionPending = false; this.confirmOpen = false; if (this.handleMutation(response, 'Приглашение отозвано')) this.requestRole() }
    this.onUserRemoved = (response) => { this.actionPending = false; this.confirmOpen = false; if (this.handleMutation(response, 'Сотрудник исключен')) this.requestRole() }
    this.emitter.on('didReceiveRoleInfoById', this.onRole)
    this.emitter.on('didUpdateRoleInfoById', this.onUpdated)
    this.emitter.on('didInviteUserToOrg', this.onInvited)
    this.emitter.on('didDeleteRoleFromOrganisation', this.onDeleted)
    this.emitter.on('onCancelInvitation', this.onInviteCancelled)
    this.emitter.on('onRemoveUserFromOrg', this.onUserRemoved)
    if (this.connection.isAuthenticated) this.requestRole()
    else this.emitter.on('connectionReady', this.requestRole)
  },
  beforeUnmount() {
    this.emitter.off('didReceiveRoleInfoById', this.onRole)
    this.emitter.off('didUpdateRoleInfoById', this.onUpdated)
    this.emitter.off('didInviteUserToOrg', this.onInvited)
    this.emitter.off('didDeleteRoleFromOrganisation', this.onDeleted)
    this.emitter.off('onCancelInvitation', this.onInviteCancelled)
    this.emitter.off('onRemoveUserFromOrg', this.onUserRemoved)
    this.emitter.off('connectionReady', this.requestRole)
  },
  methods: {
    requestRole() { this.state = 'loading'; this.connection.getRoleInfoById(this.connection.makeRequestId('role-load'), this.roleId) },
    applyRole(response) {
      if (response.error || !response.role) {
        this.state = 'error'; this.stateTitle = 'Не удалось загрузить рабочее место'; this.stateMessage = 'Повторите запрос или вернитесь к структуре.'; return
      }
      const role = response.role
      this.roleTitle = role.role || ''; this.isRootRole = role.root === true; this.permissions = normalizeRolePermissions(role.permissions)
      this.currentUser = role.has_user && role.user ? role.user : { ...EMPTY_USER }
      this.hasInvite = !!(role.invite && role.invite.empty === false)
      this.oldInviteEmail = role.invite?.email || ''; this.oldInviteToken = role.invite?.token || ''; this.inviteDisplayName = role.invite?.display_name || ''
      this.state = 'ready'
    },
    saveRole() {
      if (!this.roleTitle || this.isRootRole) return
      this.saving = true
      this.connection.updateRoleInfoById(this.connection.makeRequestId('role-save'), this.roleId, { role_title: this.roleTitle, ...normalizeRolePermissions(this.permissions) })
    },
    inviteUser() {
      if (!this.isInviteValid) return
      this.actionPending = true
      this.connection.inviteUserToOrg(this.connection.makeRequestId('invite'), this.inviteEmail, this.roleId, this.inviteFirstName, this.inviteLastName)
    },
    validateEmail(value) { return this.validateEmailValue(value) || 'Введите корректный email' },
    validateEmailValue(value) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value || '') },
    openConfirm(type) { this.confirmType = type; this.confirmOpen = true },
    confirmAction() {
      this.actionPending = true
      const requestId = this.connection.makeRequestId(this.confirmType)
      if (this.confirmType === 'delete-role') this.connection.deleteRoleFromOrganisation(requestId, this.roleId)
      if (this.confirmType === 'remove-user') this.connection.removeUserFromOrg(requestId, this.currentUser.id, this.roleId)
      if (this.confirmType === 'cancel-invite') this.connection.cancelInvitation(requestId, this.roleId)
    },
    handleMutation(response, message, refresh = false) {
      this.confirmOpen = false
      if (response?.error) { this.$q.notify({ color: 'negative', message: response.message || 'Не удалось выполнить действие' }); return false }
      this.$q.notify({ color: 'positive', message })
      if (refresh) this.requestRole()
      return true
    },
    async copyInviteLink() {
      if (!this.oldInviteToken) { this.$q.notify({ color: 'negative', message: 'Ссылка приглашения недоступна' }); return }
      await navigator.clipboard.writeText(`${window.location.origin}/#/public/invite/${this.oldInviteToken}`)
      this.$q.notify({ color: 'positive', message: 'Ссылка приглашения скопирована' })
    }
  }
}
</script>

<style scoped>
.occupant-row { display: flex; align-items: center; gap: var(--sid-space-3); max-width: 52rem; padding: var(--sid-space-4); border: 1px solid var(--sid-color-line); border-radius: var(--sid-radius-large); }
.occupant-row__content { display: flex; min-width: 0; flex: 1; flex-direction: column; }
.occupant-row__content span { overflow-wrap: anywhere; color: var(--sid-color-text-secondary); }
.occupant-row__actions { display: flex; gap: var(--sid-space-2); }
.invite-form { display: grid; max-width: 52rem; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--sid-space-3); align-items: start; }
.invite-form :deep(.q-field:nth-child(3)) { grid-column: 1 / -1; }
.role-title { max-width: 32rem; }
.role-actions { display: flex; justify-content: space-between; gap: var(--sid-space-2); margin-top: var(--sid-space-6); }
@media (max-width: 599px) {
  .occupant-row, .occupant-row__actions, .role-actions { align-items: stretch; flex-direction: column; }
  .invite-form { grid-template-columns: 1fr; }
  .invite-form :deep(.q-field:nth-child(3)) { grid-column: auto; }
}
</style>
